/**
 * @fileoverview Pure adapters that map event-detail API payloads onto the
 * organisms the Event v2 spine still carries after WEB-726 (social card,
 * nearby rails, related shelf). Extracted from `EventDetailTemplate` so the
 * cutover can delete that template without losing the honesty rules those
 * adapters encode (no fabricated social tags, no object-as-hours crash, no
 * dead `/category/` see-all hrefs).
 */

import { isValidTimezone } from '@loci/types';

import type { NearbyItem, NearbyTabId } from '@/components/organisms/NearbyRecommendationsCard';
import type {
  SocialDualStateAuthData,
  SocialDualStateFriend,
} from '@/components/organisms/SocialDualStateCard';
import type { RelatedShelfEvent } from '@/components/organisms/RelatedShelf';
import type {
  EventDetailCategory,
  EventNearbyItem,
  EventNearbyResponse,
  EventRelatedItem,
  EventRelatedResponse,
  EventSocialFriend,
  EventSocialResponse,
} from '@/types/eventDetail';

const PRICE_LEVEL_GLYPHS = ['$', '$$', '$$$', '$$$$'] as const;

const NEARBY_TAB_TYPE_DEFAULTS: Readonly<Record<NearbyTabId, string>> = {
  eat: 'Restaurant',
  drink: 'Bar',
  stay: 'Stay',
};

/**
 * Map an API friend onto the card's friend shape. The API supplies only
 * `id`, `displayName`, and `avatarUrl` — no relationship tag and no subtitle.
 * WEB-414 forbids fabricating social proof, so neither field is set.
 */
function mapSocialFriend(friend: EventSocialFriend): SocialDualStateFriend {
  const initials =
    friend.displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((token) => token.charAt(0).toUpperCase())
      .join('') || friend.displayName.charAt(0).toUpperCase();
  return {
    initials: initials || '··',
    name: friend.displayName,
  };
}

export function buildSocialAuthData(social: EventSocialResponse): SocialDualStateAuthData {
  // Defense-in-depth (API-1436): a backend regression that emits `null`
  // (or a non-array) must degrade to the empty-state card, never crash.
  const friendsGoing = (Array.isArray(social.friendsGoing) ? social.friendsGoing : []).map(
    (friend) => mapSocialFriend(friend),
  );

  const tasteFitPercent = social.tasteFitPercent ?? null;
  const localityLabel = social.crowdInsight?.topNeighborhoods?.[0];
  const ageRange = social.crowdInsight?.ageBuckets?.[0]?.label;
  const crowdInsight =
    localityLabel && ageRange ? { ageRange, localityLabel: localityLabel.toUpperCase() } : null;

  return {
    friendsGoing,
    tasteFitPercent,
    crowdInsight,
  };
}

function priceLevelGlyph(priceLevel: number | null | undefined): string {
  if (priceLevel === null || priceLevel === undefined) return '';
  const idx = Math.min(Math.max(priceLevel, 1), PRICE_LEVEL_GLYPHS.length) - 1;
  return PRICE_LEVEL_GLYPHS[idx]!;
}

function isOpenFromHours(hours: string | null | undefined): boolean {
  if (!hours) return false;
  return /open|until|till|hrs/i.test(hours);
}

/**
 * Coerce the contract's `EventNearbyItem.hours` into an optional display
 * string. A backend regression once shipped `hours` as an object, and
 * rendering that object as a React child threw React error #31.
 */
function coerceNearbyHoursLabel(hours: unknown): string | undefined {
  if (typeof hours === 'string') {
    const trimmed = hours.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (hours !== null && typeof hours === 'object') {
    const { open, close, isOpen } = hours as {
      open?: unknown;
      close?: unknown;
      isOpen?: unknown;
    };
    if (typeof open === 'string' && typeof close === 'string') {
      return isOpen === true ? `Open until ${close}` : `${open} to ${close}`;
    }
  }
  return undefined;
}

function buildMapsHref(
  item: EventNearbyItem,
  location: { address?: string | null; city?: string | null; state?: string | null } | null,
): string {
  const query = encodeURIComponent(
    [item.name, location?.address, location?.city, location?.state].filter(Boolean).join(', '),
  );
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
}

function mapNearbyItem(
  item: EventNearbyItem,
  tab: NearbyTabId,
  location: { address?: string | null; city?: string | null; state?: string | null } | null,
): NearbyItem {
  const priceGlyph = priceLevelGlyph(item.priceLevel);
  const typeParts = [item.type || NEARBY_TAB_TYPE_DEFAULTS[tab]];
  if (priceGlyph) typeParts.push(priceGlyph);
  const hoursLabel = coerceNearbyHoursLabel(item.hours);
  const rating = typeof item.rating === 'number' ? item.rating : undefined;
  const mapsUrl = typeof item.mapsUrl === 'string' && item.mapsUrl.length > 0 ? item.mapsUrl : null;
  const source = item.source === 'places' || item.source === 'curated' ? item.source : undefined;
  const distanceMiles = typeof item.distanceMiles === 'number' ? item.distanceMiles : undefined;
  return {
    id: item.id,
    name: item.name,
    type: typeParts.join(' · '),
    ...(rating !== undefined ? { rating } : {}),
    ...(distanceMiles !== undefined ? { distanceMiles } : {}),
    ...(hoursLabel !== undefined ? { hoursLabel, isOpen: isOpenFromHours(hoursLabel) } : {}),
    isEditorPick: item.isEditorPick ?? false,
    ...(source !== undefined ? { source } : {}),
    href: mapsUrl ?? buildMapsHref(item, location),
  };
}

export function buildNearbyTabs(
  nearby: EventNearbyResponse | null,
  location: { address?: string | null; city?: string | null; state?: string | null } | null,
): { eat: NearbyItem[]; drink: NearbyItem[]; stay: NearbyItem[] } {
  const empty = { eat: [], drink: [], stay: [] };
  if (!nearby) return empty;
  return {
    eat: nearby.eat.map((item) => mapNearbyItem(item, 'eat', location)),
    drink: nearby.drink.map((item) => mapNearbyItem(item, 'drink', location)),
    stay: nearby.stay.map((item) => mapNearbyItem(item, 'stay', location)),
  };
}

/**
 * Project a related-event timestamp onto a server-supplied IANA zone.
 * Invalid timestamps and invalid zones return '' — `toLocaleDateString`
 * does not throw on Invalid Date, it prints "Invalid Date".
 */
function formatRelatedDate(iso: string, timeZone: string): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime()) || !isValidTimezone(timeZone)) {
    return '';
  }
  try {
    return instant
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone })
      .toUpperCase();
  } catch {
    return '';
  }
}

function mapRelatedItem(item: EventRelatedItem, timeZone: string): RelatedShelfEvent {
  const metaParts = [item.venueName, item.priceText].filter(Boolean);
  return {
    href: `/event/${item.slug}`,
    dateLabel: formatRelatedDate(item.date, timeZone),
    title: item.title,
    metaLabel: metaParts.join(' · ').toUpperCase(),
  };
}

export function buildRelatedShelfProps({
  related,
  primaryCategory,
  cityCode,
  timezone,
  toDiscover = () => '/discover',
}: {
  related: EventRelatedResponse | null;
  primaryCategory: EventDetailCategory | null;
  cityCode: string;
  /** Server-authoritative IANA timezone for the related-event dates. */
  timezone: string;
  /**
   * Builds the uncategorised "See all" Discover link (`useDiscoverHref`), so a
   * signed-in viewer stays on `app.*` (WEB-1231 / R25). Defaults to the
   * anonymous city-host `/discover`.
   */
  toDiscover?: (query?: string) => string;
}): {
  events: RelatedShelfEvent[];
  totalInCategoryCount: number;
  categoryLabel: string;
  seeAllHref: string;
} {
  const events = (related?.events ?? []).map((item) => mapRelatedItem(item, timezone));
  const categoryLabel = (primaryCategory?.name ?? 'events').toUpperCase();
  const trimmedCategorySlug = primaryCategory?.slug.trim() ?? '';
  return {
    events,
    totalInCategoryCount: events.length,
    categoryLabel: `${categoryLabel} ${cityCode.toUpperCase()}`.trim(),
    seeAllHref:
      trimmedCategorySlug.length > 0
        ? `/category/${encodeURIComponent(trimmedCategorySlug)}`
        : toDiscover(),
  };
}

export function sourceFeedLabelOf(
  event: {
    editorial?: { sourceFeed?: string | null } | null;
    source?: { displayName?: string | null; name?: string | null } | null;
  },
  venueName: string,
): string {
  return (
    event.editorial?.sourceFeed ?? event.source?.displayName ?? event.source?.name ?? venueName
  );
}

/**
 * WEB-1101 / WEB-1098 — the resale verdict for a Ticketmaster resale
 * (TicketsNow, `tmr`) listing:
 *
 * - `true`  — the server says resale: the DS 1.10.0 resale treatment renders
 *   (marker, demoted price, `.m-trust--resale` copy).
 * - `false` — the server says primary: the primary trust copy renders.
 * - `null`  — the payload cannot say (one predating the flag). The trust
 *   footer then withholds its primary-only claims (official feed, never above
 *   face) — the absence of a resale signal is not proof of a primary listing —
 *   but asserts nothing about resale either, and no marker renders.
 *
 * Signals, in order of preference — each is the server's verdict from the
 * same PIP-343 predicate:
 * 1. `editorial.resaleListing` — the detail-v2 enrichment.
 * 2. the base payload's top-level `resaleListing` — still present when the
 *    enrichment failed and the route fell back to the base payload.
 * A `ctaType` of `resale` always counts as resale. The CTA alone is never
 * enough to call a row primary: a resale row whose CTA resolved to the venue
 * box office carries `ctaType: 'buy'`.
 */
export function resaleVerdictOf(event: {
  editorial?: { resaleListing?: boolean | null } | null;
  resaleListing?: boolean | null;
  ctaType?: string | null;
}): boolean | null {
  if (event.ctaType === 'resale') return true;
  const verdict = event.editorial?.resaleListing ?? event.resaleListing;
  return typeof verdict === 'boolean' ? verdict : null;
}
